import React, { useEffect, useState } from "react";
import api from "../../api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const VendorStockReport = () => {
  const schoolId = localStorage.getItem("school_id");
  const [vendors, setVendors] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalVendors: 0, totalQty: 0, totalValue: 0, totalPaid: 0, totalDue: 0 });
  const [filters, setFilters] = useState({ vendor_id: "", page: 1, per_page: 500 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // ================= Fetch Vendors =================
  const fetchVendors = async () => {
    try {
      const res = await api.get("/vendor/list");
      setVendors(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // ================= Fetch Report =================
  const fetchReport = async () => {
    try {
      setLoading(true);
      const res = await api.get("/product/vendor/list", {
        params: { ...filters, school_id: schoolId },
      });
      const json = res.data;
      if (json?.status === "success") {
        const data = json.data || [];
        setRows(data);
        setTotal(json.total || 0);

        // 🔹 Summary calculation
        let totalQty = 0,
          totalValue = 0,
          totalPaid = 0,
          totalDue = 0;
        const vendorSet = new Set();

        data.forEach((r) => {
          totalQty += Number(r.total_purchase_qty || 0);
          totalValue += Number(r.total_purchase_value || 0);
          totalPaid += Number(r.total_paid || 0);
          totalDue += Number(r.total_due || 0);
          vendorSet.add(r.vendor_id);
        });

        setSummary({
          totalVendors: vendorSet.size,
          totalQty,
          totalValue,
          totalPaid,
          totalDue,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    if (schoolId) fetchReport();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [filters.page, filters.vendor_id]);

  const totalPages = Math.max(1, Math.ceil(total / filters.per_page));

  // ================= Export Handlers =================
  const handleExportCSV = () => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vendor Report");
    XLSX.writeFile(wb, "vendor_report.csv");
  };

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vendor Report");
    XLSX.writeFile(wb, "vendor_report.xlsx");
  };

const handleExportPDF = () => {
  const doc = new jsPDF();
  doc.setFontSize(14);
  doc.text("Vendor-wise Stock & Payment Report", 14, 16);
  doc.setFontSize(10);
  doc.text(`Total Vendors: ${summary.totalVendors}`, 14, 24);

  const tableData = rows.map((r) => [
    r.vendor_name,
    r.product_name,
    r.variant_name,
    r.total_purchase_qty,
    r.total_purchase_value,
    r.total_paid,
    r.total_due,
  ]);

  autoTable(doc, {
    head: [["Vendor", "Product", "Variant", "Qty", "Value ₹", "Paid ₹", "Due ₹"]],
    body: tableData,
    startY: 30,
    styles: { fontSize: 9, cellPadding: 2 },
    theme: "striped",
  });

  doc.save("vendor_report.pdf");
};


  // ================= Render =================
  return (
    <div style={pageContainer}>
      {/* HEADER */}
      <header style={headerBox}>
        <h2 style={pageTitle}>📊 Vendor-wise Stock & Payment Report</h2>

        <div style={filterBox}>
          <select
            name="vendor_id"
            value={filters.vendor_id}
            onChange={(e) => setFilters({ ...filters, vendor_id: e.target.value, page: 1 })}
            style={dropdown}
          >
            <option value="">All Vendors</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>

          {/* Export Buttons */}
          <div style={{ display: "flex", gap: 8 }}>
            <button style={btnExport} onClick={handleExportCSV}>CSV</button>
            <button style={btnExport} onClick={handleExportExcel}>Excel</button>
            <button style={btnExport} onClick={handleExportPDF}>PDF</button>
          </div>
        </div>
      </header>

      {/* SUMMARY SECTION */}
      <section style={summaryBox}>
        <div style={{ ...summaryCard, background: "#dbeafe", color: "#1e3a8a" }}>
          <h4 style={summaryTitle}>Total Vendors</h4>
          <p style={summaryValue}>{summary.totalVendors}</p>
        </div>
        <div style={{ ...summaryCard, background: "#dcfce7", color: "#166534" }}>
          <h4 style={summaryTitle}>Total Qty</h4>
          <p style={summaryValue}>{summary.totalQty.toLocaleString()}</p>
        </div>
        <div style={{ ...summaryCard, background: "#fef9c3", color: "#854d0e" }}>
          <h4 style={summaryTitle}>Total Value (₹)</h4>
          <p style={summaryValue}>{summary.totalValue.toLocaleString()}</p>
        </div>
        <div style={{ ...summaryCard, background: "#ccfbf1", color: "#115e59" }}>
          <h4 style={summaryTitle}>Total Paid (₹)</h4>
          <p style={summaryValue}>{summary.totalPaid.toLocaleString()}</p>
        </div>
        <div style={{ ...summaryCard, background: "#fee2e2", color: "#991b1b" }}>
          <h4 style={summaryTitle}>Total Due (₹)</h4>
          <p style={summaryValue}>{summary.totalDue.toLocaleString()}</p>
        </div>
      </section>

      {/* TABLE SECTION */}
      <div style={cardContainer}>
        {loading ? (
          <p style={{ textAlign: "center", padding: 20 }}>Loading data...</p>
        ) : rows.length > 0 ? (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>Vendor</th>
                <th style={th}>Product</th>
                <th style={th}>Variant</th>
                <th style={thCenter}>Qty</th>
                <th style={thRight}>Value (₹)</th>
                <th style={thRight}>Paid (₹)</th>
                <th style={thRight}>Due (₹)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={i % 2 ? rowAlt : rowDefault}>
                  <td style={td}>{r.vendor_name}</td>
                  <td style={td}>{r.product_name}</td>
                  <td style={td}>{r.variant_name}</td>
                  <td style={tdCenter}>{r.total_purchase_qty}</td>
                  <td style={tdRight}>{r.total_purchase_value}</td>
                  <td style={{ ...tdRight, color: "#0f766e" }}>{r.total_paid}</td>
                  <td style={{ ...tdRight, color: "#b91c1c", fontWeight: 600 }}>{r.total_due}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ textAlign: "center", padding: 20, color: "#6b7280" }}>No data available</p>
        )}
      </div>

      {/* PAGINATION */}
      {total > 0 && (
        <div style={paginationBox}>
          <button
            onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, f.page - 1) }))}
            disabled={filters.page <= 1}
            style={{
              ...btn,
              backgroundColor: filters.page <= 1 ? "#e5e7eb" : "#0ea5e9",
              color: filters.page <= 1 ? "#94a3b8" : "#fff",
            }}
          >
            Prev
          </button>

          <span style={pageIndicator}>
            Page <strong>{filters.page}</strong> of {totalPages}
          </span>

          <button
            onClick={() => setFilters((f) => ({ ...f, page: Math.min(totalPages, f.page + 1) }))}
            disabled={filters.page >= totalPages}
            style={{
              ...btn,
              backgroundColor: filters.page >= totalPages ? "#e5e7eb" : "#0ea5e9",
              color: filters.page >= totalPages ? "#94a3b8" : "#fff",
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// ================= STYLES =================
const pageContainer = {
  minHeight: "100vh",
  background: "linear-gradient(to bottom right, #f9fafb, #eef2ff)",
  padding: "30px 20px",
};

const headerBox = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: "#fff",
  padding: "16px 20px",
  borderRadius: 12,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  marginBottom: 20,
  flexWrap: "wrap",
  gap: 10,
};

const pageTitle = { fontSize: 22, fontWeight: 700, color: "#0f172a" };
const filterBox = { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" };
const dropdown = {
  padding: "10px 14px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  background: "#fff",
  fontSize: 15,
};

const btnExport = {
  background: "#0ea5e9",
  color: "#fff",
  padding: "8px 14px",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
  transition: "0.3s",
};

const summaryBox = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: 16,
  marginBottom: 20,
};

const summaryCard = {
  borderRadius: 12,
  padding: "18px 16px",
  textAlign: "center",
  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
};
const summaryTitle = { margin: 0, fontSize: 14, fontWeight: 600, opacity: 0.8 };
const summaryValue = { margin: 0, fontSize: 22, fontWeight: 800 };

const cardContainer = {
  background: "#fff",
  borderRadius: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
  overflowX: "auto",
};

const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 15 };
const th = {
  padding: "12px 10px",
  background: "#f1f5f9",
  color: "#334155",
  fontWeight: 700,
  textAlign: "left",
  borderBottom: "2px solid #e2e8f0",
};
const thRight = { ...th, textAlign: "right" };
const thCenter = { ...th, textAlign: "center" };
const td = { padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#1e293b",textAlign: "left" };
const tdRight = { ...td, textAlign: "right" };
const tdCenter = { ...td, textAlign: "center" };
const rowAlt = { background: "#f9fafb", transition: "0.2s" };
const rowDefault = { background: "#fff", transition: "0.2s" };

const paginationBox = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 12,
  marginTop: 24,
};

const btn = {
  padding: "8px 16px",
  borderRadius: 8,
  border: "none",
  cursor: "pointer",
  fontWeight: 600,
  transition: "0.3s",
};

const pageIndicator = { fontSize: 15, color: "#475569" };

export default VendorStockReport;
